<template>
  <div class="vendor-side-menu vendor-side-menu--glass">
    <div class="vendor-side-menu-header">
      <div class="vendor-side-menu-count">
        <span v-if="totalCount > 0">
          <span class="count-display">
            {{ filteredVendors.length }} of {{ totalCount }} vendor{{ totalCount !== 1 ? 's' : '' }}
          </span>
          <span v-if="totalCount > 100" class="large-dataset-indicator" title="Large dataset - using virtual scrolling">
            ({{ Math.round((vendors.length / totalCount) * 100) }}% loaded)
          </span>
        </span>
        <span v-else-if="loading">Loading...</span>
        <span v-else>0 vendors</span>
      </div>
      <button 
        class="vendor-side-menu-hide-btn" 
        @click="hideSideMenu"
        title="Hide Vendor List"
        aria-label="Hide Vendor List"
      >
        <i class="bi bi-x"></i> Hide
      </button>
    </div>
    
    <!-- Progress bar for large datasets -->
    <div v-if="totalCount > 100 && vendors.length < totalCount" class="loading-progress-container">
      <div class="loading-progress-bar">
        <div 
          class="loading-progress-fill" 
          :style="{ width: (vendors.length / totalCount) * 100 + '%' }"
        ></div>
      </div>
      <div class="loading-progress-text">
        Loading: {{ vendors.length.toLocaleString() }} / {{ totalCount.toLocaleString() }}
      </div>
    </div>
    
    <!-- Search Section -->
    <div
      class="vendor-side-menu-search"
      :class="{ 'vendor-side-menu-search--has-badge': searchQuery && filteredVendors.length > 0 }"
    >
      <div class="vendor-side-menu-search-field">
        <span class="vendor-side-menu-search-icon" aria-hidden="true">
          <i class="bi bi-search"></i>
        </span>
        <input 
          :value="searchQuery" 
          @input="$emit('search-changed', $event.target.value)" 
          class="vendor-side-menu-search-input" 
          type="text" 
          placeholder="Search vendor..." 
        />
      </div>
      <span v-if="searchQuery && filteredVendors.length > 0" class="search-results-badge">
        {{ filteredVendors.length }} found
      </span>
    </div>
    
    <!-- Pagination Controls for Large Datasets -->
    <SideMenuPagination
      :currentPage="currentPage"
      :totalCount="totalCount"
      :recordsPerPage="recordsPerPage"
      :loading="loading"
      @page-changed="handlePageChange"
    />
    
    <!-- Scroll position indicator for large lists (clickable to jump) -->
    <div v-if="totalCount > 50 && filteredVendors.length > 0" class="scroll-position-indicator">
      <div class="scroll-position-bar" @click="jumpToScrollPosition" ref="scrollBar">
        <div 
          class="scroll-position-thumb" 
          :style="{ left: scrollPercentage + '%' }"
          :title="`Click to jump to position: ${Math.round(scrollPercentage)}%`"
        ></div>
      </div>
      <div class="scroll-position-text">
        {{ currentPositionText }}
      </div>
    </div>
    
    <div 
      class="vendor-side-menu-table-container" 
      ref="scrollContainer"
    >
      <!-- Virtual scrolling container with dynamic height -->
      <div 
        class="virtual-scroll-container"
        :style="{ height: totalHeight + 'px' }"
      >
        <!-- Spacer for items before visible range -->
        <div class="virtual-scroll-spacer" :style="{ height: offsetY + 'px' }"></div>
        
        <!-- Visible items container -->
        <table class="vendor-side-menu-table">
          <thead>
            <tr>
              <th class="vendor-name-header">Name</th>
              <th class="vendor-balance-header">Balance</th>
            </tr>
          </thead>
          <tbody>
            <!-- Loading skeleton -->
            <template v-if="loading && vendors.length === 0">
              <tr v-for="i in 10" :key="`skeleton-${i}`" class="skeleton-row" :style="{ height: rowHeight + 'px' }">
                <td>
                  <div class="skeleton-loader skeleton-loader-name"></div>
                </td>
                <td>
                  <div class="skeleton-loader skeleton-loader-balance"></div>
                </td>
              </tr>
            </template>
            
            <tr v-else-if="filteredVendors.length === 0 && !loading">
              <td colspan="2" class="vendor-side-menu-no-vendors">
                <div class="empty-state">
                  <div class="empty-state-icon-wrap" aria-hidden="true">
                    <i class="bi bi-person-fill"></i>
                  </div>
                  <div class="empty-state-text">You do not have any Vendors</div>
                  <div v-if="searchQuery" class="empty-state-hint">Try adjusting your search query</div>
                </div>
              </td>
            </tr>
            
            <!-- Virtual scrolling: Only render visible items -->
            <template v-else>
              <tr 
                v-for="vendor in visibleVendors" 
                :key="`vendor-${vendor.id}`" 
                @click="selectVendor(vendor.id)" 
                :class="{ 'vendor-side-menu-selected': displaySelectedVendor && displaySelectedVendor.id === vendor.id }"
                class="vendor-row"
                :style="{ height: rowHeight + 'px' }"
              >
                <td
                  class="vendor-name-cell"
                  :title="(vendor.vendorName || vendor.name || '-') + (showPresence ? (vendor.is_online ? ' · Online' : ' · Offline') : '')"
                >
                  <div class="vendor-side-menu-name-content">
                    <span
                      class="vendor-side-menu-vendor-icon"
                      :class="{ 'vendor-side-menu-vendor-icon--inactive': vendor.active === false }"
                      aria-hidden="true"
                    >
                      <i class="bi bi-person-fill" aria-hidden="true"></i>
                      <span
                        v-if="showPresence"
                        class="vendor-side-menu-presence-dot"
                        :class="vendor.is_online ? 'vendor-side-menu-presence-dot--online' : 'vendor-side-menu-presence-dot--offline'"
                      />
                    </span>
                    <span class="vendor-side-menu-name-copy">
                      <span class="vendor-side-menu-name-text">
                        {{ vendor.vendorName || vendor.name || '-' }}
                      </span>
                      <span
                        v-if="showPresence"
                        class="vendor-side-menu-presence-label"
                        :class="vendor.is_online ? 'vendor-side-menu-presence-label--online' : 'vendor-side-menu-presence-label--offline'"
                      >
                        {{ vendor.is_online ? 'Online' : 'Offline' }}
                      </span>
                    </span>
                  </div>
                </td>
                <td class="vendor-balance-cell">
                  <span 
                    v-if="vendor.balance !== undefined && vendor.balance !== null" 
                    :class="['balance-amount', getBalanceClass(vendor.balance)]"
                  >
                    ${{ formatBalance(vendor.balance) }}
                  </span>
                  <span v-else class="balance-empty">-</span>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
        
        <!-- Intersection Observer target for infinite scroll -->
        <div 
          ref="loadMoreTrigger"
          class="load-more-trigger"
        ></div>
        
        <!-- Loading more indicator -->
        <div 
          v-if="loadingMore" 
          class="loading-more-indicator"
        >
          <div class="loading-more-content">
            <div class="spinner"></div>
            <span>Loading more vendors...</span>
            <span v-if="totalCount > 0" class="loading-more-progress">
              ({{ vendors.length.toLocaleString() }} / {{ totalCount.toLocaleString() }})
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import SideMenuPagination from '@/components/SideMenuPagination.vue';

export default {
  name: 'VendorSideMenu',
  components: {
    SideMenuPagination
  },
  props: {
    vendors: {
      type: Array,
      default: () => []
    },
    selectedVendor: {
      type: Object,
      default: null
    },
    vendorFilter: {
      type: String,
      default: 'Active'
    },
    vendorRoleFilter: {
      type: String,
      default: ''
    },
    searchQuery: {
      type: String,
      default: ''
    },
    loading: {
      type: Boolean,
      default: false
    },
    totalCount: {
      type: Number,
      default: 0
    },
    loadingMore: {
      type: Boolean,
      default: false
    },
    currentPage: {
      type: Number,
      default: 1
    },
    recordsPerPage: {
      type: Number,
      default: 100
    },
    vendorScope: {
      type: String,
      default: 'individual',
      validator: (v) => ['all', 'individual'].includes(v)
    },
    showPresence: {
      type: Boolean,
      default: false
    }
  },
  emits: ['vendor-selected', 'search-changed', 'load-more', 'page-changed', 'hide-side-menu'],
  data() {
    return {
      scrollTop: 0,
      visibleBuffer: 15, // Increased buffer for smoother scrolling
      containerHeight: 0,
      intersectionObserver: null,
      resizeObserver: null,
      rafId: null, // RequestAnimationFrame ID for scroll optimization
      showScrollIndicator: false
    };
  },
  computed: {
    rowHeight() {
      return this.showPresence ? 56 : 48;
    },
    displaySelectedVendor() {
      if (this.vendorScope === 'individual') {
        return this.selectedVendor;
      }
      if (this.vendorScope === 'all' && this.selectedVendor) {
        return this.selectedVendor;
      }
      return null;
    },
    filteredVendors() {
      // Use memoization for better performance with large arrays
      if (!this.vendors || this.vendors.length === 0) return [];
      
      let filtered = this.vendors;
      
      // Filter by active status
      if (this.vendorFilter === 'Active') {
        filtered = filtered.filter(v => v && (v.active !== false && v.active !== null));
      } else if (this.vendorFilter === 'Inactive') {
        filtered = filtered.filter(v => v && v.active === false);
      }

      if (this.vendorRoleFilter) {
        filtered = filtered.filter((v) => v && v.vendorRole === this.vendorRoleFilter);
      }
      
      // Filter by search query (client-side for loaded data)
      if (this.searchQuery && this.searchQuery.trim()) {
        const query = this.searchQuery.toLowerCase().trim();
        filtered = filtered.filter(v => {
          if (!v) return false;
          const name = (v.vendorName || v.name || '').toLowerCase();
          const vendorId = (v.vendorId || '').toLowerCase();
          const email = (v.email || '').toLowerCase();
          return name.includes(query) || vendorId.includes(query) || email.includes(query);
        });
      }

      // Keep selected vendor at top of the list (individual / scoped selection)
      if (this.displaySelectedVendor && filtered.length > 0) {
        const selectedId = this.displaySelectedVendor.id;
        const selectedIndex = filtered.findIndex((v) => v && v.id === selectedId);
        if (selectedIndex > 0) {
          const selected = filtered[selectedIndex];
          filtered = [selected, ...filtered.filter((v) => v && v.id !== selectedId)];
        }
      }

      return filtered;
    },
    // Calculate total height for virtual scrolling
    totalHeight() {
      return this.filteredVendors.length * this.rowHeight;
    },
    // Calculate offset for items before visible range
    offsetY() {
      return Math.max(0, this.startIndex * this.rowHeight);
    },
    // Calculate visible range based on scroll position
    startIndex() {
      const index = Math.floor(this.scrollTop / this.rowHeight);
      return Math.max(0, index - this.visibleBuffer);
    },
    endIndex() {
      if (!this.containerHeight) return this.filteredVendors.length;
      
      const visibleCount = Math.ceil(this.containerHeight / this.rowHeight);
      const index = this.startIndex + visibleCount + (this.visibleBuffer * 2);
      return Math.min(this.filteredVendors.length, index);
    },
    // Get only visible vendors for rendering
    visibleVendors() {
      return this.filteredVendors.slice(this.startIndex, this.endIndex);
    },
    // Check if there are more vendors to load
    hasMoreToLoad() {
      return this.vendors.length < this.totalCount;
    },
    // Calculate scroll percentage
    scrollPercentage() {
      if (!this.$refs.scrollContainer || this.containerHeight === 0) return 0;
      const scrollHeight = this.$refs.scrollContainer.scrollHeight;
      const clientHeight = this.$refs.scrollContainer.clientHeight;
      const maxScroll = scrollHeight - clientHeight;
      if (maxScroll <= 0) return 0;
      const percentage = (this.scrollTop / maxScroll) * 100;
      return Math.max(0, Math.min(100, percentage)); // Clamp between 0 and 100
    },
    // Current position text
    currentPositionText() {
      if (this.filteredVendors.length === 0) return '';
      const start = Math.min(this.startIndex + 1, this.filteredVendors.length);
      const end = Math.min(this.endIndex, this.filteredVendors.length);
      return `${start}-${end} of ${this.filteredVendors.length.toLocaleString()}`;
    },
    // Check if at bottom
    isAtBottom() {
      if (!this.$refs.scrollContainer) return false;
      const container = this.$refs.scrollContainer;
      const threshold = 5; // 5px threshold for better detection
      const scrollBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
      return scrollBottom <= threshold;
    }
  },
  methods: {
    selectVendor(vendorId) {
      this.$emit('vendor-selected', vendorId);
      this.$nextTick(() => {
        this.scrollSelectedToTop();
      });
    },
    scrollSelectedToTop() {
      if (!this.$refs.scrollContainer) return;
      this.$refs.scrollContainer.scrollTop = 0;
      this.scrollTop = 0;
    },
    formatBalance(balance) {
      if (balance === null || balance === undefined) return '0.00';
      const num = Number(balance);
      return Math.abs(num).toLocaleString('en-US', { 
        minimumFractionDigits: 2, 
        maximumFractionDigits: 2 
      });
    },
    getBalanceClass(balance) {
      if (balance === null || balance === undefined) return 'zero';
      const num = Number(balance);
      if (num > 0) return 'positive';
      if (num < 0) return 'negative';
      return 'zero';
    },
    // Optimized scroll handler using requestAnimationFrame
    handleScroll() {
      if (this.rafId) {
        cancelAnimationFrame(this.rafId);
      }
      
      this.rafId = requestAnimationFrame(() => {
        if (!this.$refs.scrollContainer) return;
        this.scrollTop = this.$refs.scrollContainer.scrollTop;
        this.rafId = null;
      });
    },
    // Update container height
    updateContainerHeight() {
      this.$nextTick(() => {
        if (this.$refs.scrollContainer) {
          this.containerHeight = this.$refs.scrollContainer.clientHeight;
        }
      });
    },
    // Setup Intersection Observer for infinite scroll
    setupIntersectionObserver() {
      if (!this.$refs.loadMoreTrigger || !window.IntersectionObserver) {
        return;
      }
      
      // Cleanup existing observer
      if (this.intersectionObserver) {
        this.intersectionObserver.disconnect();
      }
      
      // Create new observer
      this.intersectionObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting && this.hasMoreToLoad && !this.loadingMore) {
              this.$emit('load-more');
            }
          });
        },
        {
          root: this.$refs.scrollContainer,
          rootMargin: '100px', // Trigger 100px before reaching the bottom
          threshold: 0.1
        }
      );
      
      this.intersectionObserver.observe(this.$refs.loadMoreTrigger);
    },
    // Setup Resize Observer for container height
    setupResizeObserver() {
      if (!this.$refs.scrollContainer || !window.ResizeObserver) {
        return;
      }
      
      // Cleanup existing observer
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
      }
      
      // Create new observer
      this.resizeObserver = new ResizeObserver(() => {
        this.updateContainerHeight();
      });
      
      this.resizeObserver.observe(this.$refs.scrollContainer);
    },
    // Handle page change from pagination component
    handlePageChange(page) {
      this.$emit('page-changed', page);
      // Reset scroll to top when changing pages
      this.$nextTick(() => {
        this.scrollSelectedToTop();
      });
    },
    hideSideMenu() {
      this.$emit('hide-side-menu');
    },
    // Jump to scroll position (click on scroll indicator)
    jumpToScrollPosition(event) {
      if (!this.$refs.scrollContainer || !this.$refs.scrollBar) return;
      
      const scrollHeight = this.$refs.scrollContainer.scrollHeight;
      const clientHeight = this.$refs.scrollContainer.clientHeight;
      const maxScroll = scrollHeight - clientHeight;
      
      if (maxScroll <= 0) return;
      
      const rect = this.$refs.scrollBar.getBoundingClientRect();
      const clickX = event.clientX - rect.left;
      const percentage = Math.max(0, Math.min(100, (clickX / rect.width) * 100));
      
      const targetScroll = (percentage / 100) * maxScroll;
      
      this.$refs.scrollContainer.scrollTop = targetScroll;
    }
  },
  watch: {
    vendors() {
      this.updateContainerHeight();
      this.$nextTick(() => {
        this.setupIntersectionObserver();
      });
    },
    filteredVendors() {
      this.updateContainerHeight();
    },
    loadingMore(newVal) {
      if (!newVal) {
        // When loading completes, check if we need to load more
        this.$nextTick(() => {
          this.setupIntersectionObserver();
        });
      }
    },
    currentPage() {
      // Reset scroll position when page changes
      this.$nextTick(() => {
        this.scrollSelectedToTop();
      });
    },
    'displaySelectedVendor.id'(newId, oldId) {
      if (newId != null && newId !== oldId) {
        this.$nextTick(() => {
          this.scrollSelectedToTop();
        });
      }
    }
  },
  mounted() {
    this.updateContainerHeight();
    this.setupIntersectionObserver();
    this.setupResizeObserver();
    
    // Fallback to scroll event if Intersection Observer not available
    if (!window.IntersectionObserver && this.$refs.scrollContainer) {
      this.$refs.scrollContainer.addEventListener('scroll', this.handleScroll, { passive: true });
    } else if (this.$refs.scrollContainer) {
      // Use scroll event for scroll position tracking
      this.$refs.scrollContainer.addEventListener('scroll', this.handleScroll, { passive: true });
    }
    
    // Update container height on window resize
    window.addEventListener('resize', this.updateContainerHeight);
  },
  beforeUnmount() {
    // Cleanup observers
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    
    // Cleanup event listeners
    window.removeEventListener('resize', this.updateContainerHeight);
    if (this.$refs.scrollContainer) {
      this.$refs.scrollContainer.removeEventListener('scroll', this.handleScroll);
    }
    
    // Cancel animation frame
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
    }
  }
};
</script>

<style scoped>
@import '../styles/VendorSideMenu.css';
@import '../styles/vendor-side-menu-glass.css';
</style>
